import "react-calendar/dist/Calendar.css";
import "react-time-picker/dist/TimePicker.css";

import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  ConversationStatus,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { CollapseSubsection } from "@redotech/redo-web/card";
import { DateRangeValue } from "@redotech/redo-web/date-range";
import { Modal, ModalSize } from "@redotech/redo-web/modal";
import { ProgressBar } from "@redotech/redo-web/progress-bar";
import { TextInput } from "@redotech/redo-web/text-input";
import { getDateString } from "@redotech/util/date-format";
import { Dispatch, memo, SetStateAction, useState } from "react";
import Calendar from "react-calendar";
import TimePicker from "react-time-picker";
import "../calendar.module.css";
import {
  bulkSnoozeConversations,
  updateConversation,
} from "../client/conversations";
import { UpdateConversationStateContext } from "../context/update-conversations-context";
import { getNextBatch } from "./../utils";
import * as snoozeModalCss from "./snooze-modal.module.css";

type CalendarView = "century" | "decade" | "year" | "month";

export const SnoozeModal = memo(function SnoozeModal({
  open,
  setOpen,
  conversations,
  setActiveConversation,
  setConversationsInProximity,
  inDetailView,
  cleanupFn,
  selectAllInfo,
  customOnOptionClick,
  nextConversationInList,
  prevConversationInList,
}: {
  open: boolean;
  setOpen(val: boolean): void;
  conversations: MinimalConversation[];
  setActiveConversation: (
    conversation: MinimalConversation | undefined,
  ) => void;
  setConversationsInProximity?: Dispatch<SetStateAction<MinimalConversation[]>>;
  inDetailView: boolean;
  cleanupFn?: (clearSelected?: boolean) => void;
  selectAllInfo?: {
    count: number;
    deselectedConversations: MinimalConversation[];
    bulkActionIterator: AsyncIterator<{ conversations: MinimalConversation[] }>;
    setBlockRefresh: (val: boolean) => void;
  };
  customOnOptionClick?: (snoozeDate: Date) => void;
  nextConversationInList?: MinimalConversation;
  prevConversationInList?: MinimalConversation;
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const { removeConversation } = useRequiredContext(
    UpdateConversationStateContext,
  );
  const [dateTimeValue, setDateTimeValue] = useState<Date | null>(
    conversations.length === 1 &&
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      conversations[0].snoozedUntil &&
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      conversations[0].snoozedUntil > Date()
      ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        new Date(conversations[0].snoozedUntil)
      : null,
  );
  const [snoozingSelectAll, setSnoozingSelectAll] = useState(false);
  const [numDone, setNumDone] = useState(0);

  const now = new Date();

  const oneHour = new Date(now);
  oneHour.setHours(oneHour.getHours() + 1);

  const threeHours = new Date(now);
  threeHours.setHours(threeHours.getHours() + 3);

  const sixHours = new Date(now);
  sixHours.setHours(sixHours.getHours() + 6);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(8, 0, 0);

  const threeDays = new Date();
  threeDays.setDate(threeDays.getDate() + 3);
  threeDays.setHours(8, 0, 0);

  const oneWeek = new Date();
  oneWeek.setDate(oneWeek.getDate() + 7);
  oneWeek.setHours(8, 0, 0);

  // Next Monday at 8 AM (if we're past weekend, will be next week's Monday)
  const nextMonday = new Date();
  nextMonday.setDate(
    nextMonday.getDate() + ((1 + 7 - nextMonday.getDay()) % 7 || 7),
  );
  nextMonday.setHours(8, 0, 0);

  const presetDates: { label: string; date: Date }[] = [
    { label: "1 hour", date: oneHour },
    { label: "3 hours", date: threeHours },
    { label: "6 hours", date: sixHours },
    { label: "1 day", date: tomorrow },
    { label: "3 days", date: threeDays },
    { label: "1 week", date: oneWeek },
  ];

  // Add next Monday if it's not the same as any of the existing dates
  if (
    !presetDates.some((date) => date.date.getTime() === nextMonday.getTime())
  ) {
    presetDates.push({ label: "Next Monday", date: nextMonday });
  }

  const sortedPresetDates = presetDates.sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );

  const handleSetSnooze = async (snoozedUntil: Date) => {
    if (conversations.length === 1) {
      setConversationsInProximity?.((prev: MinimalConversation[]) =>
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        prev.filter((conv) => conversations[0]._id !== conv._id),
      );
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      void updateConversation(client, conversations[0], {
        snoozedUntil: snoozedUntil.toISOString(),
      });
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      removeConversation(conversations[0]);
      amplitude.logEvent("snooze-conversation", {
        mode: "single",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        conversationIds: [conversations[0]._id],
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        channels: [conversations[0].platform],
      });
      if (inDetailView) {
        setActiveConversation(nextConversationInList || prevConversationInList);
      }
    } else if (conversations.length > 1) {
      const conversationIds = conversations
        .filter(
          (conversation) => conversation.status !== ConversationStatus.CLOSED,
        )
        .map((conversation) => conversation._id);
      if (conversationIds.length > 0) {
        await bulkSnoozeConversations(client, {
          conversationIds,
          snoozedUntil: snoozedUntil.toISOString(),
        });
        const channels = [
          ...new Set(
            conversations.map((conversation) => conversation.platform),
          ),
        ];
        amplitude.logEvent("snooze-conversation", {
          mode: "multiple",
          conversationIds,
          channels,
        });
      }
    }
    cleanupFn?.(true);
  };

  const snoozeBatch = async (
    snoozedUntil: Date,
  ): Promise<{ batchSize: number; done: boolean }> => {
    if (!selectAllInfo) {
      return { batchSize: 0, done: true };
    }
    const { done, value } = await getNextBatch(
      selectAllInfo.bulkActionIterator,
    );
    if (done) {
      return { batchSize: 0, done };
    }
    const conversationIds = value.conversations
      .filter(
        (conversation) =>
          conversation.status !== ConversationStatus.CLOSED &&
          !selectAllInfo.deselectedConversations.some(
            (deselectedConversation) =>
              deselectedConversation._id === conversation._id,
          ),
      )
      .map((conversation) => conversation._id);
    if (conversationIds.length > 0) {
      await bulkSnoozeConversations(client, {
        conversationIds,
        snoozedUntil: snoozedUntil.toISOString(),
      });
    }
    return { batchSize: value.conversations.length, done: false };
  };

  // This function synchronously snoozes each page of conversations.
  // If speed becomes an issue, we could asynchronously snooze each page.
  const handleSetSnoozeSelectAll = async (snoozedUntil: Date) => {
    if (!selectAllInfo) {
      return;
    }
    setSnoozingSelectAll(true);
    selectAllInfo.setBlockRefresh(true);
    let done = false;
    do {
      const result = await snoozeBatch(snoozedUntil);
      setNumDone((prevNumDone) => prevNumDone + result.batchSize);
      done = result.done;
    } while (!done);
    amplitude.logEvent("snooze-conversation", { mode: "all" });
    setSnoozingSelectAll(false);
    selectAllInfo.setBlockRefresh(false);
    cleanupFn?.(true);
  };

  const handleOptionClick = async (snoozeDate: Date | undefined) => {
    if (snoozeDate) {
      if (customOnOptionClick) {
        customOnOptionClick(snoozeDate);
      } else if (selectAllInfo) {
        await handleSetSnoozeSelectAll(snoozeDate);
      } else {
        await handleSetSnooze(snoozeDate);
      }
      setOpen(false);
      setDateTimeValue(null);
    }
  };

  const formatDate = (date: Date | null) => {
    if (date) {
      return getDateString(date);
    } else {
      return "";
    }
  };

  const handleSetCalendar = (date: Date | null) => {
    if (!date) {
      setDateTimeValue(null);
      return;
    }
    const newDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    if (dateTimeValue) {
      newDate.setHours(dateTimeValue.getHours());
      newDate.setMinutes(dateTimeValue.getMinutes());
    }
    setDateTimeValue(newDate);
  };

  const handleSetClock = (time: string | null) => {
    if (!time) {
      setDateTimeValue(null);
      return;
    }
    const newDate = new Date();
    const timePieces = time.split(":");
    const hours = timePieces[0];
    const minutes = timePieces[1];
    newDate.setHours(Number(hours));
    newDate.setMinutes(Number(minutes));
    if (dateTimeValue) {
      newDate.setFullYear(dateTimeValue.getFullYear());
      newDate.setDate(dateTimeValue.getDate());
      newDate.setMonth(dateTimeValue.getMonth());
    }
    setDateTimeValue(newDate);
  };

  const handleCustomDateSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    if (dateTimeValue) {
      if (selectAllInfo) {
        await handleSetSnoozeSelectAll(dateTimeValue);
      } else {
        await handleSetSnooze(dateTimeValue);
      }
    }
    setOpen(false);
    setDateTimeValue(null);
  };

  return (
    <Modal
      onClose={() => {
        cleanupFn?.();
        setOpen(false);
      }}
      open={open}
      showHeaderBorder={false}
      size={ModalSize.SMALL}
      title={
        snoozingSelectAll
          ? "Snoozing tickets"
          : `Snooze ${
              selectAllInfo
                ? "all selected tickets "
                : conversations.length > 1
                  ? conversations?.length + " tickets "
                  : ""
            }until`
      }
    >
      {snoozingSelectAll ? (
        <ProgressBar
          value={(numDone / (selectAllInfo?.count || numDone)) * 100}
        />
      ) : (
        <div className={snoozeModalCss.wrapper}>
          {sortedPresetDates.map((presetDate) => (
            <Button
              key={presetDate.label}
              onClick={async () => {
                await handleOptionClick(presetDate.date);
              }}
            >
              <SnoozeOptionText
                text={presetDate.label}
                time={presetDate.date}
              />
            </Button>
          ))}
          <hr className={snoozeModalCss.line} />
          <div className={snoozeModalCss.collapseContainer}>
            <CollapseSubsection defaultCollapsed title="Pick date & time">
              <div className={snoozeModalCss.dateTimeWrapper}>
                <form onSubmit={handleCustomDateSubmit}>
                  <Calendar
                    onChange={(value: DateRangeValue) =>
                      !Array.isArray(value) && handleSetCalendar(value)
                    }
                    tileClassName={({
                      date,
                      view,
                    }: {
                      date: Date;
                      view: CalendarView;
                    }) => {
                      if (view === "month") {
                        const now = new Date();
                        const today = new Date(
                          now.getFullYear(),
                          now.getMonth(),
                          now.getDate(),
                        );
                        const tileDate = new Date(
                          date.getFullYear(),
                          date.getMonth(),
                          date.getDate(),
                        );
                        if (tileDate < today) {
                          return snoozeModalCss.disabledTile;
                        } else if (tileDate.getTime() === today.getTime()) {
                          return snoozeModalCss.todayTile;
                        }
                      }
                      return undefined;
                    }}
                    tileDisabled={({
                      date,
                      view,
                    }: {
                      date: Date;
                      view: CalendarView;
                    }) => {
                      if (view === "month") {
                        const today = new Date();
                        const todayWithoutTime = new Date(
                          today.getFullYear(),
                          today.getMonth(),
                          today.getDate(),
                        );
                        return date < todayWithoutTime;
                      }
                      return false;
                    }}
                    value={dateTimeValue}
                  />
                  <div className={snoozeModalCss.dateTimeInputContainer}>
                    <TextInput readonly value={formatDate(dateTimeValue)} />
                    <TimePicker
                      disableClock
                      onChange={handleSetClock}
                      value={dateTimeValue}
                    />
                  </div>
                  <div className={snoozeModalCss.dateTimeButtonsContainer}>
                    <Button theme={ButtonTheme.PRIMARY} type="submit">
                      Save
                    </Button>
                  </div>
                </form>
              </div>
            </CollapseSubsection>
          </div>
        </div>
      )}
    </Modal>
  );
});

export const SnoozeOptionText = memo(function SnoozeOptionText({
  text,
  time,
}: {
  text: string;
  time: Date;
}) {
  const formattedTime = time.toLocaleString([], {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return (
    <div className={snoozeModalCss.snoozeOption}>
      <p>{text}</p>
      <p className={snoozeModalCss.time}>{formattedTime}</p>
    </div>
  );
});
